import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PROFILE } from "@/constants/testIds";
import { toast } from "sonner";

export default function ProfileForm({ onCreated }) {
  const [form, setForm] = useState({
    name: "", dob: "", tob: "", place_name: "", lat: null, lon: null, tz: "",
    ayanamsha: "lahiri",
  });
  const [places, setPlaces] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const searchPlace = async (q) => {
    setForm(f => ({ ...f, place_name: q }));
    if (q.length < 3) { setPlaces([]); return; }
    setSearching(true);
    try {
      const r = await api.geocode(q);
      setPlaces(r.results || []);
    } catch (e) { toast.error("Geocoding failed"); }
    setSearching(false);
  };

  const pickPlace = (p) => {
    setForm(f => ({ ...f, place_name: p.display, lat: p.lat, lon: p.lon, tz: p.tz }));
    setPlaces([]);
  };

  const submit = async () => {
    if (!form.name || !form.dob || !form.tob || !form.lat) {
      toast.error("Fill all fields including a picked place"); return;
    }
    setSaving(true);
    try {
      const p = await api.createProfile(form);
      toast.success("Profile created");
      onCreated?.(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
    setSaving(false);
  };

  return (
    <div className="rounded-md border border-[#2A2D35] bg-[#181A1F] p-6 space-y-5">
      <h2 className="font-heading text-2xl text-[#E6E1D6]">New Birth Profile</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Name</Label>
          <Input data-testid={PROFILE.nameInput} value={form.name}
                 onChange={e => setForm(f => ({...f, name: e.target.value}))}
                 className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]" />
        </div>
        <div>
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Date of Birth</Label>
          <Input data-testid={PROFILE.dobInput} type="date" value={form.dob}
                 onChange={e => setForm(f => ({...f, dob: e.target.value}))}
                 className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono" />
        </div>
        <div>
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Time of Birth (24h)</Label>
          <Input data-testid={PROFILE.tobInput} type="time" value={form.tob}
                 onChange={e => setForm(f => ({...f, tob: e.target.value}))}
                 className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono" />
        </div>
        <div>
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Ayanamsha</Label>
          <Select value={form.ayanamsha} onValueChange={(v) => setForm(f => ({...f, ayanamsha: v}))}>
            <SelectTrigger data-testid={PROFILE.ayanamshaSelect}
                           className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              <SelectItem value="lahiri">Lahiri (default)</SelectItem>
              <SelectItem value="raman">Raman</SelectItem>
              <SelectItem value="kp">KP</SelectItem>
              <SelectItem value="sayana">Sayana (Tropical)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 relative">
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Place of Birth</Label>
          <Input data-testid={PROFILE.placeInput} value={form.place_name}
                 onChange={e => searchPlace(e.target.value)}
                 placeholder="City, state, country"
                 className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]" />
          {places.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-[#181A1F] border border-[#2A2D35] rounded-md shadow-xl max-h-60 overflow-auto">
              {places.map((p) => (
                <div key={`${p.name}-${p.lat}-${p.lon}`} data-testid={`${PROFILE.placeSuggestion}-${p.name}`}
                     onClick={() => pickPlace(p)}
                     className="px-4 py-2 text-sm cursor-pointer hover:bg-[#0F1115] border-b border-[#2A2D35] last:border-0">
                  <div className="text-[#E6E1D6]">{p.display}</div>
                  <div className="text-xs text-[#6E6B64] font-mono">
                    {p.lat.toFixed(3)}, {p.lon.toFixed(3)} · {p.tz}
                  </div>
                </div>
              ))}
            </div>
          )}
          {form.tz && (
            <div className="mt-2 text-xs text-[#6E6B64] font-mono">
              tz: {form.tz} · {form.lat?.toFixed(3)},{form.lon?.toFixed(3)}
            </div>
          )}
        </div>
      </div>
      <div className="pt-2">
        <Button data-testid={PROFILE.saveBtn} onClick={submit} disabled={saving}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] transition-colors duration-200">
          {saving ? "Casting…" : "Cast Chart"}
        </Button>
      </div>
    </div>
  );
}
